import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  Phone,
  MessageSquare,
  Globe,
  Shield,
  Heart,
  AlertTriangle,
  ExternalLink
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CrisisResources = ({ user }) => {
  const navigate = useNavigate();
  const [resources, setResources] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      const response = await axios.get(`${API}/crisis/resources`);
      setResources(response.data);
    } catch (error) {
      console.error("Failed to load crisis resources:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const logReferral = async (type) => {
    try {
      await axios.post(`${API}/crisis/referral`, {
        referral_type: type,
        notes: "User accessed crisis resource from app"
      });
    } catch (error) {
      console.error("Failed to log referral:", error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-red-600 text-white sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-red-700">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Crisis Resources
                </h1>
                <p className="text-sm text-red-100">
                  Immediate help is available
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Disclaimer Banner */}
      <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-start gap-3">
          <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Important:</strong> VALORLink provides peer support, not clinical crisis intervention. 
            If you or someone you know is in immediate danger, please contact the resources below.
          </p>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Primary Resource - 988 */}
        {resources?.primary && (
          <Card className="card-valor mb-6 border-2 border-red-200 bg-red-50">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Badge className="bg-red-600">Primary Resource</Badge>
              </div>
              <CardTitle className="text-2xl text-red-800 mt-2">
                {resources.primary.name}
              </CardTitle>
              <CardDescription className="text-red-700">
                {resources.primary.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <a
                href={`tel:${resources.primary.number}`}
                onClick={() => logReferral("988")}
                className="block"
                data-testid="call-988-btn"
              >
                <Button size="lg" className="w-full bg-red-600 hover:bg-red-700 text-xl h-16">
                  <Phone className="w-6 h-6 mr-3" />
                  Call {resources.primary.number}
                </Button>
              </a>
              
              {resources.primary.veterans_press && (
                <p className="mt-3 text-center text-red-800 font-semibold">
                  📞 {resources.primary.veterans_press}
                </p>
              )}
              
              {resources.primary.url && (
                <a 
                  href={resources.primary.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 mt-3 text-red-700 hover:text-red-900"
                >
                  <Globe className="w-4 h-4" />
                  Visit website
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </CardContent>
          </Card>
        )}

        {/* Veterans Specific Resource */}
        {resources?.veterans_specific && (
          <Card className="card-valor mb-6 border-2 border-blue-200 bg-blue-50">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-600">Veterans Specific</Badge>
              </div>
              <CardTitle className="text-xl text-blue-800 mt-2">
                {resources.veterans_specific.name}
              </CardTitle>
              <CardDescription className="text-blue-700">
                {resources.veterans_specific.description}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <a
                href={`tel:${resources.veterans_specific.number}`}
                onClick={() => logReferral("veterans_crisis_line")}
                className="block"
                data-testid="call-veterans-line-btn"
              >
                <Button size="lg" className="w-full bg-blue-600 hover:bg-blue-700">
                  <Phone className="w-5 h-5 mr-2" />
                  Call {resources.veterans_specific.number} ({resources.veterans_specific.press})
                </Button>
              </a>
              
              <div className="grid grid-cols-2 gap-3">
                <a href={`sms:${resources.veterans_specific.text}`} className="block">
                  <Button variant="outline" className="w-full border-blue-300 text-blue-700">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Text {resources.veterans_specific.text}
                  </Button>
                </a>
                
                <a 
                  href={resources.veterans_specific.chat}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Button variant="outline" className="w-full border-blue-300 text-blue-700">
                    <Globe className="w-4 h-4 mr-2" />
                    Online Chat
                  </Button>
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Services */}
        {resources?.emergency && (
          <Card className="card-valor mb-6">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <CardTitle>{resources.emergency.name}</CardTitle>
              </div>
              <CardDescription>
                {resources.emergency.description}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <a
                href={`tel:${resources.emergency.number}`}
                onClick={() => logReferral("911")}
                className="block"
                data-testid="call-911-btn"
              >
                <Button variant="destructive" size="lg" className="w-full">
                  <Phone className="w-5 h-5 mr-2" />
                  Call {resources.emergency.number}
                </Button>
              </a>
            </CardContent>
          </Card>
        )}

        {/* Additional Resources */}
        {resources?.additional && resources.additional.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-primary" />
              Additional Resources
            </h2>
            <div className="space-y-3">
              {resources.additional.map((resource, index) => (
                <Card key={index} className="card-valor">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold">{resource.name}</h3>
                        <p className="text-sm text-muted-foreground">{resource.description}</p>
                      </div>
                      {resource.number && (
                        <a href={`tel:${resource.number.replace(/\D/g, '')}`}>
                          <Button variant="outline" size="sm">
                            <Phone className="w-4 h-4 mr-1" />
                            Call
                          </Button>
                        </a>
                      )}
                      {resource.text && (
                        <span className="text-sm font-medium text-primary">{resource.text}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* VALORLink Info */}
        <Card className="card-valor bg-muted/50">
          <CardContent className="p-6 text-center">
            <Shield className="w-10 h-10 text-primary mx-auto mb-3" />
            <h3 className="font-semibold mb-2">About VALORLink Peer Support</h3>
            <p className="text-sm text-muted-foreground">
              VALORLink connects veterans with trained peer responders for non-clinical support. 
              Our responders are here to listen, but we are not a substitute for professional 
              crisis intervention. If you're in crisis, please use the resources above.
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CrisisResources;
